/**
 * Shared regex patterns for citation/reference handling
 * Used by both fixMalformedReferences and sanitizeCitations
 */
const CITATION_PATTERNS = {
  // UUID-based references: [any text](uuid@timestamp)
  uuidRef: /\[[^\]]+\]\([a-f0-9-]+@\d+(?:\.\d+)?\)/g,
  // Numeric timestamp references: [any text](timestamp)
  numericRef: /\[[^\]]+\]\(\d+(?:\.\d+)?\)/g,
};

/**
 * Removes horizontal spaces around citation references
 * Preserves newlines while cleaning up spacing
 */
const cleanCitationSpacing = (text: string): string => {
  let result = text;

  // Remove spaces before and after UUID-based references
  result = result.replace(
    new RegExp(`[ \\t]+(${CITATION_PATTERNS.uuidRef.source})[ \\t]+`, 'g'),
    '$1',
  );
  result = result.replace(
    new RegExp(`[ \\t]+(${CITATION_PATTERNS.uuidRef.source})`, 'g'),
    '$1',
  );
  result = result.replace(
    new RegExp(`(${CITATION_PATTERNS.uuidRef.source})[ \\t]+`, 'g'),
    '$1',
  );

  // Remove spaces before and after numeric references
  result = result.replace(
    new RegExp(`[ \\t]+(${CITATION_PATTERNS.numericRef.source})[ \\t]+`, 'g'),
    '$1',
  );
  result = result.replace(
    new RegExp(`[ \\t]+(${CITATION_PATTERNS.numericRef.source})`, 'g'),
    '$1',
  );
  result = result.replace(
    new RegExp(`(${CITATION_PATTERNS.numericRef.source})[ \\t]+`, 'g'),
    '$1',
  );

  return result;
};

/**
 * Sanitizes markdown content to fix common formatting issues
 * Removes only the outer wrapping markdown code block if the entire content
 * is wrapped in one (e.g., when LLM returns ```markdown ... ```)
 *
 * NOTE: This function intentionally preserves legitimate code blocks
 * within the content - it only removes the outer wrapper.
 *
 * @param markdown - The markdown text to sanitize
 * @param options - Optional settings for sanitization
 * @param options.stripImages - Whether to remove markdown image syntax (default: true)
 * @returns Cleaned markdown text
 */
export const sanitizeMarkdown = (
  markdown: string,
  options?: { stripImages?: boolean },
): string => {
  const { stripImages = true } = options || {};

  // Only remove wrapping markdown code block if the ENTIRE content is wrapped
  // This preserves legitimate code blocks inside the content
  let sanitized = markdown.replace(
    /^```\s*(?:markdown)?\n([\s\S]*?)\n```$/g,
    '$1',
  );

  // Replace em dashes with comma and space
  sanitized = sanitized.replace(/—/g, ', ');

  // Strip image markdown if enabled (images are displayed via screenshots array)
  if (stripImages) {
    // Remove markdown images: ![alt](url)
    sanitized = sanitized.replace(/!\[[^\]]*\]\([^)]+\)/g, '');

    // Also remove any broken/partial image markdown that spans lines
    sanitized = sanitized.replace(/!\[[^\]]*\]\s*\([^)]*$/gm, '');

    // Clean up multiple consecutive newlines that might result
    sanitized = sanitized.replace(/\n{3,}/g, '\n\n');
  }

  // Trim any extra whitespace that might have been left
  return sanitized.trim();
};

/**
 * Replaces em dashes with comma and space
 *
 * @param text - The text containing em dashes
 * @returns Text with em dashes replaced
 */
export const replaceEmDashes = (text: string): string => {
  if (!text) return '';
  return text.replace(/—/g, ', ');
};

/**
 * Fixes missing paragraph breaks in text
 * Detects sentence-ending punctuation directly followed by a word character
 * without proper spacing (indicates tool call continuation without newline)
 *
 * @param text - The text that may have missing paragraph breaks
 * @returns Text with paragraph breaks added where needed
 *
 * @example
 * fixMissingParagraphBreaks("end of sentence.Start of new") // Returns "end of sentence.\n\nStart of new"
 */
export const fixMissingParagraphBreaks = (text: string): string => {
  if (!text) return '';

  // Match sentence-ending punctuation (. ! ? :) followed by optional citation references,
  // then directly followed by an uppercase letter (start of new sentence)
  //
  // IMPORTANT: Requires a lowercase letter BEFORE the punctuation to avoid:
  // - Acronyms: "U.S.A" won't match (uppercase before period)
  // - Abbreviations: "Dr.Smith" won't match (r is lowercase, but we want real sentences)
  //
  // This pattern accounts for:
  // 1. Simple case: "sentence.Next" -> "sentence.\n\nNext"
  // 2. With colon: "content:Based on" -> "content:\n\nBased on"
  // 3. With citation: "sentence.[→](uuid@123)Next" -> "sentence.[→](uuid@123)\n\nNext"
  //
  // The regex looks for:
  // - A lowercase letter before punctuation ([a-z]) - ensures real sentence end
  // - Sentence ending punctuation ([.!?:])
  // - Optional citation reference(s) (the [...](...)* pattern)
  // - Directly followed by uppercase letter ([A-Z])
  const citationPattern = '(?:\\[[^\\]]*\\]\\([^)]*\\))*';
  const pattern = new RegExp(`([a-z])([.!?:])(${citationPattern})([A-Z])`, 'g');

  return text.replace(pattern, '$1$2$3\n\n$4');
};

/**
 * Fixes malformed citation references in markdown text
 * Handles both UUID-based and numeric timestamp references
 *
 * @param text - The text containing potentially malformed references
 * @returns Text with fixed reference syntax
 *
 * @example
 * fixMalformedReferences("[→](uuid@123]") // Returns "[→](uuid@123)"
 * fixMalformedReferences("[→](47]")      // Returns "[→](47)"
 */
export const fixMalformedReferences = (text: string): string => {
  if (!text) return '';

  // Replace em dashes first
  let fixedText = replaceEmDashes(text);

  // Fix UUID-based references with missing closing parenthesis: [→](uuid@timestamp] -> [→](uuid@timestamp)
  fixedText = fixedText.replace(
    /\[→\]\(([a-f0-9-]+@\d+(?:\.\d+)?)\]/g,
    '[→]($1)',
  );

  // Fix regular timestamp references with bracket closing: [→](47] -> [→](47)
  fixedText = fixedText.replace(/\[→\]\((\d+(?:\.\d+)?)\]/g, '[→]($1)');

  // Use shared helper to clean citation spacing
  fixedText = cleanCitationSpacing(fixedText);

  // Fix missing paragraph breaks (from tool call continuations)
  fixedText = fixMissingParagraphBreaks(fixedText);

  return fixedText;
};

/**
 * Cleans up citation formatting by removing extra spaces and line breaks
 * Used to normalize citation arrows in markdown content
 *
 * @param text - The text containing citations
 * @returns Text with normalized citation formatting
 */
export const sanitizeCitations = (text: string): string => {
  // Replace em dashes first
  const sanitized = replaceEmDashes(text);

  // Use shared helper to clean citation spacing and trim
  return cleanCitationSpacing(sanitized).trim();
};

/**
 * Strips markdown image syntax from text
 * Removes ![alt text](url) patterns since images are displayed via screenshots array
 * Also handles multiline markdown images where the LLM splits the syntax across lines
 *
 * @param text - The text containing markdown images
 * @returns Text with image markdown removed
 *
 * @example
 * stripImageMarkdown("Here's an image: ![Screenshot](http://example.com/img.jpg)")
 * // Returns "Here's an image: "
 */
export const stripImageMarkdown = (text: string): string => {
  if (!text) return '';

  let result = text;

  // First, handle multiline markdown images where URL is split across lines
  // Pattern: ![alt text]\n(url split\nacross\nlines)
  // This uses [\s\S] to match any character including newlines
  result = result.replace(/!\[[^\]]*\][\s]*\([^)]*\)/g, '');

  // Handle case where URL continues on next lines without closing paren
  // Look for ![...]( followed by lines that look like URL continuations
  // and ending with a line that has )
  result = result.replace(
    /!\[[^\]]*\]\s*\(\s*https?:\/\/[^\s)]*[\s\S]*?\)/g,
    '',
  );

  // Remove standard single-line markdown images: ![alt](url)
  result = result.replace(/!\[[^\]]*\]\([^)]+\)/g, '');

  // Remove orphaned image markdown parts that might be left:
  // - ![text] not followed by ( - likely broken markdown
  result = result.replace(/!\[[^\]]*\](?!\s*\()/g, '');

  // - Orphaned (url) that starts a line (leftover from split image)
  result = result.replace(/^\s*\(https?:\/\/[^)]*\)\s*$/gm, '');

  // - Lines that are just URL fragments (common in split images)
  result = result.replace(
    /^\s*(?:https?:\/\/[^\s]*|[A-Za-z0-9%&=_-]+)\s*$/gm,
    (match) => {
      // Only remove if it looks like a URL fragment (contains URL-like characters)
      if (
        match.includes('X-Amz') ||
        match.includes('localhost') ||
        match.includes('.io/') ||
        match.includes('Signature=')
      ) {
        return '';
      }
      return match;
    },
  );

  // Clean up multiple consecutive newlines that might result
  result = result.replace(/\n{3,}/g, '\n\n');

  // Clean up trailing whitespace on lines
  result = result.replace(/[ \t]+$/gm, '');

  // Clean up lines that are just punctuation or very short (leftover fragments)
  result = result.replace(/^\s*[?&]\s*$/gm, '');

  return result.trim();
};

/**
 * Extracts unique file IDs from citation references in text
 * Parses [→](fileId@timestamp) patterns and returns unique file IDs
 *
 * @param text - The text containing citation references
 * @returns Array of unique file IDs found in the text
 *
 * @example
 * extractFileIdsFromCitations("Text [→](abc123@45) more [→](def456@90)")
 * // Returns ["abc123", "def456"]
 */
export const extractFileIdsFromCitations = (text: string): string[] => {
  if (!text) return [];

  // Match UUID-based references: [→](fileId@timestamp)
  const pattern = /\[→\]\(([a-f0-9-]+)@\d+(?:\.\d+)?\)/g;
  const matches = Array.from(text.matchAll(pattern));

  // Extract unique file IDs using reduce
  const fileIds = matches.reduce((acc, match) => {
    acc.add(match[1]);
    return acc;
  }, new Set<string>());

  return Array.from(fileIds);
};
